/**
 * Undo/redo as a past/present/future stack.
 *
 * Two eviction rules, because a naive step-count cap blows up on big images:
 *  1. at most `limit` steps are kept, and
 *  2. the retained snapshots stay under `memoryBudget` bytes.
 *
 * What a snapshot *costs* is the caller's decision, passed per push as `bytes`.
 * A document store charges the layers a commit actually created rather than the
 * whole document, because immutable snapshots share every untouched layer with
 * their neighbours and charging the lot would evict history on large files.
 *
 * The stack stores whatever the caller needs (a document snapshot, a patch, a
 * layer bitmap) — it is deliberately unaware of the document model so it stays
 * trivially testable.
 */

export interface HistoryEntry<T> {
  id: number;
  label: string;
  at: number;
  state: T;
  bytes: number;
}

export interface HistoryOptions {
  limit?: number;
  memoryBudget?: number;
  /**
   * Cost of `initial`, so the starting snapshot is charged like every other.
   * Callers pass an estimate (see `estimateDocumentBytes`).
   */
  bytes?: number;
}

export interface HistoryRow {
  id: number;
  label: string;
  /** true for the state currently applied to the document. */
  active: boolean;
  undone: boolean;
}

export class History<T> {
  private past: HistoryEntry<T>[] = [];
  private future: HistoryEntry<T>[] = [];
  private present: HistoryEntry<T>;
  private nextId = 1;
  private readonly limit: number;
  private readonly memoryBudget: number;
  private bytes = 0;

  constructor(initial: T, options: HistoryOptions = {}) {
    this.limit = options.limit ?? 60;
    this.memoryBudget = options.memoryBudget ?? Number.POSITIVE_INFINITY;
    this.present = {
      id: 0,
      label: 'Open',
      at: Date.now(),
      state: initial,
      bytes: options.bytes ?? 0,
    };
    this.bytes = this.present.bytes;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  get state(): T {
    return this.present.state;
  }

  get label(): string {
    return this.present.label;
  }

  get size(): number {
    return this.past.length + 1 + this.future.length;
  }

  /** Rows for the History panel, oldest first, with the current row marked. */
  get rows(): HistoryRow[] {
    const all = [...this.past, this.present, ...this.future];
    return all.map((entry) => ({
      id: entry.id,
      label: entry.label,
      active: entry.id === this.present.id,
      undone: entry.id > this.present.id && this.future.some((f) => f.id === entry.id),
    }));
  }

  /**
   * Record a new state. Any redoable tail is discarded (standard editor
   * behaviour: branching history is not supported on purpose).
   *
   * `bytes` is what this state costs — see the note at the top of the file.
   */
  push(state: T, label: string, bytes = 0): void {
    for (const dropped of this.future) this.bytes -= dropped.bytes;
    this.future = [];
    this.past.push(this.present);
    this.present = { id: this.nextId++, label, at: Date.now(), state, bytes };
    this.bytes += bytes;
    this.evict();
  }

  /** Undo and redo move states between stacks; nothing is freed, so the tally holds. */
  undo(): T | undefined {
    const previous = this.past.pop();
    if (!previous) return undefined;
    this.future.unshift(this.present);
    this.present = previous;
    return previous.state;
  }

  redo(): T | undefined {
    const next = this.future.shift();
    if (!next) return undefined;
    this.past.push(this.present);
    this.present = next;
    this.evict();
    return next.state;
  }

  clear(initial?: T, bytes = 0): void {
    this.past = [];
    this.future = [];
    if (initial !== undefined) {
      this.present = { id: this.nextId++, label: 'Open', at: Date.now(), state: initial, bytes };
      this.bytes = bytes;
      return;
    }
    this.present = { ...this.present, bytes: 0 };
    this.bytes = 0;
  }

  /**
   * Approximate bytes retained by the history: the undo stack, the current
   * state and the redoable tail, because all three hold their states alive.
   *
   * What each entry was charged for is the caller's business (see `push`). The
   * byte budget evicts against this, and can overshoot by the one undo step it
   * refuses to drop.
   */
  get retainedBytes(): number {
    return this.bytes;
  }

  private evict(): void {
    while (this.past.length > this.limit) {
      const dropped = this.past.shift();
      if (dropped) this.bytes -= dropped.bytes;
    }
    while (this.past.length > 1 && this.bytes > this.memoryBudget) {
      const dropped = this.past.shift();
      if (dropped) this.bytes -= dropped.bytes;
    }
  }
}
